import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Particles } from '../common/effects';
import wolfLogo from '../../assets/images/polar_ace_wolf_dark.png';
import './LoadingScreen.css';

/**
 * LoadingScreen — Cinematic post-login transition.
 *
 * Displays a full-viewport loading experience with:
 * - Centered wolf logo with intense breathing glow
 * - "Your stage is loading..." tagline
 * - Horizontal progress bar that fills over 2.5s
 *
 * @param {Object} props
 * @param {function} props.onComplete - Callback when loading animation finishes
 * @param {boolean} [props.isVisible=true] - Controls visibility for fade in/out
 * @returns {JSX.Element}
 */
function LoadingScreen({ onComplete, isVisible = true }) {
  const [progressPhase, setProgressPhase] = useState('waiting');

  useEffect(() => {
    if (!isVisible) return;

    setProgressPhase('waiting');

    /* Timeline:
     * t=0.0s  — Component mounts, wolf visible
     * t=0.5s  — Tagline fades in
     * t=0.6s  — Progress bar begins filling
     * t=3.1s  — Progress bar complete (2.5s duration)
     * t=3.4s  — Hold complete, trigger onComplete
     */

    const taglineTimer = setTimeout(() => {
      setProgressPhase('tagline');
    }, 500);

    const progressTimer = setTimeout(() => {
      setProgressPhase('filling');
    }, 600);

    const completeTimer = setTimeout(() => {
      setProgressPhase('complete');
    }, 3100);

    const exitTimer = setTimeout(() => {
      onComplete?.();
    }, 3400);

    return () => {
      clearTimeout(taglineTimer);
      clearTimeout(progressTimer);
      clearTimeout(completeTimer);
      clearTimeout(exitTimer);
    };
  }, [isVisible, onComplete]);

  return (
    <div className={`loading-screen ${isVisible ? 'loading-screen--visible' : ''}`}>
      {/* Background */}
      <div className="loading-screen__bg" aria-hidden="true" />

      {/* Particles — low density ambient effect */}
      <div className="loading-screen__particles">
        <Particles count={20} />
      </div>

      {/* Content container */}
      <div className="loading-screen__content">
        {/* Wolf logo with intense glow */}
        <div className="loading-screen__wolf-container">
          <div className="loading-screen__glow-pool" aria-hidden="true" />
          <img
            src={wolfLogo}
            alt="Polar Ace"
            className="loading-screen__wolf"
          />
        </div>

        {/* Tagline */}
        <p
          className={`loading-screen__tagline ${
            progressPhase !== 'waiting' ? 'loading-screen__tagline--visible' : ''
          }`}
        >
          Your stage is loading...
        </p>

        {/* Progress bar */}
        <div className="loading-screen__progress-track">
          <div
            className={`loading-screen__progress-fill ${
              progressPhase === 'filling' || progressPhase === 'complete'
                ? 'loading-screen__progress-fill--active'
                : ''
            } ${progressPhase === 'complete' ? 'loading-screen__progress-fill--complete' : ''}`}
          />
        </div>
      </div>
    </div>
  );
}

LoadingScreen.propTypes = {
  onComplete: PropTypes.func,
  isVisible: PropTypes.bool,
};

export default LoadingScreen;
